import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import configurationService from '../../services/configurationService';

interface DiasVencimentoSelectProps {
  value: number | string | null | undefined;
  onChange: (value: number | '') => void;
  label?: string;
  size?: 'small' | 'medium';
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  allowClear?: boolean;
  placeholder?: string;
}

const DiasVencimentoSelect: React.FC<DiasVencimentoSelectProps> = ({
  value,
  onChange,
  label = 'Dia de Vencimento',
  size = 'small',
  required = false,
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  allowClear = false,
  placeholder,
}) => {
  const [dias, setDias] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const carregarDias = async () => {
      try {
        setLoading(true);
        const config = await configurationService.getConfigurationByKey('dias_de_vencimento');
        const diasParsed = config.valor
          .split(',')
          .map((d) => parseInt(d.trim(), 10))
          .filter((d) => !isNaN(d) && d >= 1 && d <= 31)
          .sort((a, b) => a - b);
        setDias(diasParsed);
      } catch (err) {
        console.error('Erro ao carregar dias de vencimento:', err);
        setDias([5, 10, 15, 20, 25]);
      } finally {
        setLoading(false);
      }
    };

    carregarDias();
  }, []);

  const selectValue = value === null || value === undefined || value === '' ? '' : Number(value);

  return (
    <FormControl fullWidth={fullWidth} size={size} disabled={disabled || loading} error={error} required={required}>
      <InputLabel shrink={allowClear || selectValue !== ''}>{label}</InputLabel>
      <Select
        value={selectValue}
        label={label}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === '' ? '' : Number(v));
        }}
        displayEmpty={allowClear}
      >
        <MenuItem value="">
          <em>{placeholder || 'Selecione um dia'}</em>
        </MenuItem>
        {/* Evita o warning do MUI de valor fora do range durante o loading */}
        {selectValue !== '' && !dias.includes(Number(selectValue)) && (
          <MenuItem value={selectValue} sx={{ display: 'none' }}>
            Dia {String(selectValue).padStart(2, '0')}
          </MenuItem>
        )}
        {loading ? (
          <MenuItem disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2">Carregando...</Typography>
            </Box>
          </MenuItem>
        ) : dias.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Nenhum dia configurado
            </Typography>
          </MenuItem>
        ) : (
          dias.map((dia) => (
            <MenuItem key={dia} value={dia}>
              Dia {String(dia).padStart(2, '0')}
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default DiasVencimentoSelect;
